"use client";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { usePathname } from "next/navigation";
import { NewsletterConsentField } from "./NewsletterConsentField";
import { FormCard } from "@/forms/FormCard";
import { Checkbox } from "@cappelendamm/design-system/react";
import { PAYMENT_PROVIDERS } from "@cappelendamm/e-commerce-services";
import { CheckoutForm } from "@/checkout/types";
import { CHECKOUT_TERMS_TYPES } from "@/checkout/Sections/TermsSection/types";
import Link from "next/link";

type TermsSectionProps = {
  privacyPolicyText?: React.ReactNode;
  hasPurchaseTerms?: boolean;
  hasSubscriptionTerms?: boolean;
  hasBookClubTerms?: boolean;
  newsletterTerms?: {
    shouldShowNewsletterConsent?: boolean | null;
    newsletterTopicId?: string | null;
    newsletterPurposeId?: string | null;
    newsletterConsentText?: string | null;
  } | null;
};

export const TermsSection = ({
  newsletterTerms,
  hasPurchaseTerms,
  hasSubscriptionTerms,
  hasBookClubTerms,
  privacyPolicyText,
}: TermsSectionProps) => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<CheckoutForm>();
  const payment = useWatch({ control, name: "payment" });
  const rawPathname = usePathname();
  // Strip any existing /terms/<slug> suffix so links are always based on the
  // checkout/subscribe base path — even when an intercepting modal is open.
  const pathname = rawPathname.replace(/\/terms\/[^/]+$/, "");

  const isInvoice = payment?.type === PAYMENT_PROVIDERS.INVOICE;
  const canShowNewsletterConsent =
    newsletterTerms?.shouldShowNewsletterConsent &&
    newsletterTerms?.newsletterTopicId &&
    newsletterTerms?.newsletterPurposeId &&
    newsletterTerms?.newsletterConsentText;
  const canShowPurchaseTerms = isInvoice && hasPurchaseTerms;

  if (
    !canShowNewsletterConsent &&
    !privacyPolicyText &&
    !canShowPurchaseTerms &&
    (!isInvoice || !hasSubscriptionTerms) &&
    !hasBookClubTerms
  ) {
    return null;
  }

  const purchaseTermsHref = `${pathname}/terms/${CHECKOUT_TERMS_TYPES.purchase}`;
  const subscriptionTermsHref = `${pathname}/terms/${CHECKOUT_TERMS_TYPES.subscription}`;
  const newsletterTermsHref = `${pathname}/terms/${CHECKOUT_TERMS_TYPES.newsletter}`;

  return (
    <FormCard className="mt-3! border-gray-200 bg-transparent text-base">
      {privacyPolicyText && (
        <div className="[&_a]:underline [&_p]:inline">
          {privacyPolicyText}
          {canShowPurchaseTerms && (
            <>
              {" og "}
              <Link
                href={purchaseTermsHref}
                scroll={false}
                className="text-inherit underline"
              >
                vilkårene for kjøp
              </Link>
            </>
          )}
        </div>
      )}

      {!privacyPolicyText && canShowPurchaseTerms && (
        <div>
          Ved å fortsette bekrefter jeg å ha lest, forstått og godkjent{" "}
          <Link
            href={purchaseTermsHref}
            scroll={false}
            className="text-inherit underline"
          >
            vilkårene for kjøp
          </Link>
        </div>
      )}

      {hasBookClubTerms && (
        <div>
          <div className="flex items-center justify-start gap-2">
            <Checkbox
              id="bookclubTermsAgreement"
              className="hover:cursor-pointer"
              aria-invalid={!!errors.bookclubTermsAgreement}
              aria-describedby={
                errors.bookclubTermsAgreement
                  ? "bookclubTermsAgreement-error"
                  : undefined
              }
              {...register("bookclubTermsAgreement", { required: true })}
            />
            <label htmlFor="bookclubTermsAgreement">
              Jeg bekrefter å ha lest og godkjent{" "}
              <Link
                href={`${pathname}/terms/${CHECKOUT_TERMS_TYPES.bookclub}`}
                scroll={false}
                className="underline focus:[box-shadow:var(--cvr-focus)]"
              >
                vilkårene for bokklubb*
              </Link>
            </label>
          </div>
          {errors.bookclubTermsAgreement && (
            <p
              id="bookclubTermsAgreement-error"
              role="alert"
              className="text-status-error-500"
            >
              Du må godkjenne vilkårene for bokklubb*
            </p>
          )}
        </div>
      )}

      {isInvoice && hasSubscriptionTerms && (
        <div>
          <div className="flex items-center justify-start gap-2">
            <Checkbox
              id="termsAgreement"
              className="hover:cursor-pointer"
              {...register("termsAgreement", { required: true })}
            />
            <label htmlFor="termsAgreement">
              Jeg har lest og godkjenner{" "}
              <Link
                href={subscriptionTermsHref}
                scroll={false}
                className="underline focus:[box-shadow:var(--cvr-focus)]"
              >
                vilkårene*
              </Link>
            </label>
          </div>
          {errors.termsAgreement && (
            <label
              htmlFor="termsAgreement"
              className="bottom-0 inline text-status-error-500"
            >
              Du må godkjenne vilkårene*
            </label>
          )}
        </div>
      )}

      {canShowNewsletterConsent && (
        <NewsletterConsentField
          newsletterConsentText={newsletterTerms.newsletterConsentText ?? ""}
          newsletterTermsHref={newsletterTermsHref}
        />
      )}
    </FormCard>
  );
};
